import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { FaGift, FaSearch } from 'react-icons/fa';

const getBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const cleanUrl = url.replace(/\/$/, '');
  if (cleanUrl.endsWith('/api')) {
    return cleanUrl;
  }
  return `${cleanUrl}/api`;
};

const API_URL = getBaseURL();

const AdminBonus = () => {
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBonuses();
  }, []);

  const fetchBonuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/bonuses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBonuses(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching bonuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBonuses = bonuses.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isExpired = (expiryDate) => {
    return new Date() > new Date(expiryDate);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Bonus Management - Admin Dashboard</title>
      </Helmet>
      
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <FaGift className="mr-3 text-green-500" />
                Bonus Management
              </h1>
              <p className="text-gray-600 mt-1">View users who received the first-time investment bonus</p>
            </div>
            
            <div className="mt-4 md:mt-0 relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search by name, email or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">User Details</th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Bonus Amount</th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Granted Date</th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredBonuses.length > 0 ? (
                    filteredBonuses.map((user) => {
                      const expired = isExpired(user.bonus?.expiresAt);
                      return (
                        <tr key={user._id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="font-semibold text-gray-800">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="text-xs text-gray-400 mt-1">ID: {user.userId || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-bold text-green-600">₹{user.bonus?.amount?.toLocaleString() || 0}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {user.bonus?.givenAt ? new Date(user.bonus.givenAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {user.bonus?.expiresAt ? new Date(user.bonus.expiresAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              expired ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {expired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        <FaGift className="mx-auto text-4xl text-gray-300 mb-3" />
                        <p>No bonuses found.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminBonus;
